/**
 * Main Application Component - Aegis Foundation Kit
 *
 * This is the root component that orchestrates the entire Aegis application,
 * providing routing, authentication, theming, and state management. It serves
 * as the central hub for all user interactions across different user types and
 * feature domains.
 *
 * @component
 * @description
 * ## Application Architecture
 *
 * The app is structured around multiple user roles and feature domains:
 *
 * ### User Types
 * - **Public Users**: Marketing pages, pricing, help
 * - **Authenticated Users**: Main app functionality (individuals/institutions)
 * - **Partners**: Trust service providers with extended capabilities
 * - **Admins**: Platform administrators with system-wide access
 * - **Enterprise**: Organization owners with SSO and white-labeling
 *
 * ### Key Features
 * - Multi-tenant architecture with role-based access control
 * - Lazy loading for performance optimization
 * - Comprehensive error boundaries and fallback handling
 * - Internationalization support
 * - Theme management (light/dark modes)
 * - Real-time notifications and toasts
 *
 * ## Route Structure
 *
 * ### Public Routes (`/`)
 * Marketing and informational pages accessible without authentication
 *
 * ### Auth Routes (`/auth/*`)
 * Login, registration, and authentication flows
 *
 * ### App Routes (`/app/*`)
 * Main application features for end users (individuals and institutions)
 *
 * ### Partner Routes (`/partner/*`)
 * Extended functionality for trust service providers
 *
 * ### Admin Routes (`/admin/*`)
 * Platform administration and system management (restricted to platform admins)
 *
 * ### Enterprise Routes (`/enterprise/*`)
 * Organization management, SSO configuration, and white-labeling
 *
 * @example
 * ```tsx
 * // The App component is typically rendered by main.tsx
 * import App from './App';
 * // No props required - fully self-contained
 * ```
 *
 * @returns {JSX.Element} The complete Aegis application with routing and providers
 *
 * @since 1.0.0
 * @author Aegis Development Team
 * @license MIT
 */

import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Routes, Route } from "react-router-dom";

// Providers
import { QueryProvider } from "@/providers/QueryProvider";
import { ThemeProvider } from "@/providers/ThemeProvider";
import { I18nProvider } from "@/lib/i18n/i18n";
import FeedbackWidgetProvider from "@/providers/FeedbackWidgetProvider";

// Layouts
import { PublicLayout } from "@/layouts/PublicLayout";
import { AuthLayout } from "@/layouts/AuthLayout";
import { AppLayout } from "@/layouts/AppLayout";
import { PartnerLayout } from "@/layouts/PartnerLayout";
import { AdminLayout } from "@/layouts/AdminLayout";
import { EnterpriseLayout } from "@/layouts/EnterpriseLayout";

// Error boundary
import ErrorBoundary from "@/components/shell/ErrorBoundary";
import SessionProvider from "@/components/auth/SessionProvider";

// Public pages - lazy loaded for better performance
const HomePage = React.lazy(() => import("@/pages/public/HomePage"));
const HowItWorksPageLazy = React.lazy(() => import("@/pages/public/HowItWorksPage"));
const TrustPageLazy = React.lazy(() => import("@/pages/public/TrustPage"));
const AboutPageLazy = React.lazy(() => import("@/pages/public/AboutPage"));
const IndividualsPageLazy = React.lazy(() => import("@/pages/public/IndividualsPage"));
const InstitutionsPageLazy = React.lazy(() => import("@/pages/public/InstitutionsPage"));
const PartnersPageLazy = React.lazy(() => import("@/pages/public/PartnersPage"));
const StatusPageLazy = React.lazy(() => import("@/pages/public/StatusPage"));
const InsightsPageLazy = React.lazy(() => import("@/pages/public/InsightsPage"));
const PrivacyPageLazy = React.lazy(() => import("@/pages/public/PrivacyPage"));
const TermsPageLazy = React.lazy(() => import("@/pages/public/TermsPage"));
const CookiesPageLazy = React.lazy(() => import("@/pages/public/CookiesPage"));
const AIStatementPageLazy = React.lazy(() => import("@/pages/public/AIStatementPage"));
const PricingPageLazy = React.lazy(() => import("@/pages/public/PricingPage"));
const HelpPageLazy = React.lazy(() => import("@/pages/public/HelpPage"));

// Auth pages - lazy loaded for better performance
const LoginPageLazy = React.lazy(() => import("@/pages/auth/LoginPage"));
const RegisterPageLazy = React.lazy(() => import("@/pages/auth/RegisterPage"));
const UniversalOrganizationSignupPageLazy = React.lazy(() => import("@/pages/auth/UniversalOrganizationSignupPage"));
// Legacy signup pages - redirect to universal page for backward compatibility
const InstitutionSignupRedirectLazy = React.lazy(() => import("@/pages/auth/InstitutionSignupPageRedirect"));
const PartnerSignupRedirectLazy = React.lazy(() => import("@/pages/auth/PartnerSignupPageRedirect"));

// App pages
const DashboardPage = React.lazy(() => import("@/pages/app/DashboardPage"));

// Partner pages
const PartnerDashboardProductionLazy = React.lazy(() => import("@/pages/partner/Dashboard"));
const PartnerSystemDashboardLazy = React.lazy(() => import("@/pages/partner/PartnerSystemDashboard"));

// Enterprise pages
const EnterpriseSystemDashboardLazy = React.lazy(() => import("@/pages/enterprise/EnterpriseSystemDashboard"));

// Admin pages - lazy loaded for better performance
const AdminDashboardLazy = React.lazy(() => import("@/pages/admin/AdminDashboard"));
const AuditLogsLazy = React.lazy(() => import("@/pages/admin/AuditLogs"));
import RequireAuth from "@/components/auth/RequireAuth";
import RequireRole from "@/components/auth/RequireRole";
import RequireUnauthenticated from "@/components/auth/RequireUnauthenticated";
import { PARTNER_ROLES, ADMIN_ROLES } from "@/lib/rbac";

const PlanDemoLazy = React.lazy(() => import("@/pages/app/PlanDemo"));
const PlanEditorLazy = React.lazy(() => import("@/pages/app/PlanEditorPage"));
const AcceptInviteLazy = React.lazy(() => import("@/pages/invites/AcceptInvite"));
const IntegrationsPageLazy = React.lazy(() => import("@/pages/partner/ApiKeys"));
const PartnerUsersPageLazy = React.lazy(() => import("@/pages/partner/PartnerUsersPage"));
const CasesPageLazy = React.lazy(() => import("@/pages/partner/CasesPage"));
const CaseDetailPageLazy = React.lazy(() => import("@/pages/partner/CaseDetailPage"));
const AuditsPageLazy = React.lazy(() => import("@/pages/partner/AuditsPage"));
const JurisdictionsPageLazy = React.lazy(() => import("@/pages/partner/JurisdictionsPage"));
const EscalationsPageLazy = React.lazy(() => import("@/pages/partner/EscalationsPage"));
const BillingPageLazy = React.lazy(() => import("@/pages/partner/BillingPage"));
const PartnerSettingsPageLazy = React.lazy(() => import("@/pages/partner/PartnerSettingsPage"));
const PartnerWebhooksPageLazy = React.lazy(() => import("@/pages/partner/Webhooks"));
const PartnerDocumentationPageLazy = React.lazy(() => import("@/pages/partner/Documentation"));
const PartnerIncidentsPageLazy = React.lazy(() => import("@/pages/partner/IncidentsPage"));
const AssetsPageLazy = React.lazy(() => import("@/pages/app/AssetsPage"));
const RolesPageLazy = React.lazy(() => import("@/pages/app/RolesPage"));
const TriggersPageLazy = React.lazy(() => import("@/pages/app/TriggersPage"));
const InstructionsPageLazy = React.lazy(() => import("@/pages/app/InstructionsPage"));
const ActivityPageLazy = React.lazy(() => import("@/pages/app/ActivityPage"));
const DependentsPageLazy = React.lazy(() => import("@/pages/app/DependentsPage"));
const DocumentsPageLazy = React.lazy(() => import("@/pages/app/DocumentsPage"));
const SimulationPageLazy = React.lazy(() => import("@/pages/app/SimulationPage"));
const SettingsPageLazy = React.lazy(() => import("@/pages/app/SettingsPage"));
const InstitutionCasePageLazy = React.lazy(() => import("@/pages/app/InstitutionCasePage"));
const ProfilePageLazy = React.lazy(() => import("@/pages/app/ProfilePage"));
const ExecutorTasksPageLazy = React.lazy(() => import("@/pages/app/ExecutorTasksPage"));
const PlaybookTemplatesPageLazy = React.lazy(() => import("@/pages/app/PlaybookTemplatesPage"));

// Institutional evaluation pages
const InstitutionalDashboardPageLazy = React.lazy(() => import("@/pages/app/InstitutionalDashboardPage"));
const InstitutionalIntakePageLazy = React.lazy(() => import("@/pages/app/InstitutionalIntakePage"));
const InstitutionalSubmissionConfirmedPageLazy = React.lazy(() => import("@/pages/app/InstitutionalSubmissionConfirmedPage"));
const ExampleScenarioPageLazy = React.lazy(() => import("@/pages/app/ExampleScenarioPage"));

// Family & Community Mode pages
const ProofPacksPageLazy = React.lazy(() => import("@/pages/app/ProofPacksPage"));
const CapsulesPageLazy = React.lazy(() => import("@/pages/app/CapsulesPage"));
const EmergencyProtocolsPageLazy = React.lazy(() => import("@/pages/app/EmergencyProtocolsPage"));
const WitnessesPageLazy = React.lazy(() => import("@/pages/app/WitnessesPage"));
const VerifyProofPackPageLazy = React.lazy(() => import("@/pages/public/VerifyProofPackPage"));

// Trust Engine pages
const TrustNetworkPageLazy = React.lazy(() => import("@/pages/app/TrustNetworkPage"));
const AuthorityStatePageLazy = React.lazy(() => import("@/pages/app/AuthorityStatePage"));
const AbsenceStatusPageLazy = React.lazy(() => import("@/pages/app/AbsenceStatusPage"));
const CompliancePageLazy = React.lazy(() => import("@/pages/app/CompliancePage"));

// Elevated domain pages
const GuardiansPageLazy = React.lazy(() => import("@/pages/app/GuardiansPage"));
const DigitalCustodyPageLazy = React.lazy(() => import("@/pages/app/DigitalCustodyPage"));
const HealthcarePageLazy = React.lazy(() => import("@/pages/app/HealthcarePage"));
const NotarizationPageLazy = React.lazy(() => import("@/pages/app/NotarizationPage"));
const BeneficiaryDashboardPageLazy = React.lazy(() => import("@/pages/app/BeneficiaryDashboardPage"));

// Admin lazy pages
const VerificationsPageLazy = React.lazy(() => import("@/pages/admin/VerificationsPage"));
const RiskPageLazy = React.lazy(() => import("@/pages/admin/RiskPage"));
const PartnerApplicationsPageLazy = React.lazy(() => import("@/pages/admin/PartnerApplicationsPage"));
const PartnersManagementPageLazy = React.lazy(() => import("@/pages/admin/PartnersManagementPage"));
const PartnerDetailPageLazy = React.lazy(() => import("@/pages/admin/PartnerDetailPage"));
const AdminPartnerWebhooksPageLazy = React.lazy(() => import("@/pages/admin/PartnerWebhooksPage"));
const IncidentsPageLazy = React.lazy(() => import("@/pages/admin/IncidentsPage"));
const PublicStatusPublisherLazy = React.lazy(() => import("@/pages/admin/PublicStatusPublisher"));
const DataGovernancePageLazy = React.lazy(() => import("@/pages/admin/DataGovernancePage"));
const DeletionJobsPageLazy = React.lazy(() => import("@/pages/admin/DeletionJobsPage"));
const ExportsPageLazy = React.lazy(() => import("@/pages/admin/ExportsPage"));
const IntegrityDashboardLazy = React.lazy(() => import("@/pages/admin/IntegrityDashboard"));
const SecurityDashboardLazy = React.lazy(() => import("@/pages/admin/SecurityDashboard"));
const ApiManagementPageLazy = React.lazy(() => import("@/pages/admin/ApiManagementPage"));
const AnalyticsPageLazy = React.lazy(() => import("@/pages/admin/AnalyticsPage"));
const PromotionsPageLazy = React.lazy(() => import("@/pages/admin/PromotionsPage"));
const ObjectionsManagementPageLazy = React.lazy(() => import("@/pages/admin/ObjectionsManagementPage"));
const WorkerHealthPageLazy = React.lazy(() => import("@/pages/admin/WorkerHealthPage"));
const GeoIPMetricsDashboardLazy = React.lazy(() => import("@/pages/admin/GeoIPMetricsDashboard"));
const AdminSettingsPageLazy = React.lazy(() => import("@/pages/admin/AdminSettingsPage"));
const FeedbackManagerLazy = React.lazy(() => import("@/components/admin/FeedbackManager").then(m => ({ default: m.FeedbackManager })));
const FeedbackAnalyticsLazy = React.lazy(() => import("@/components/admin/FeedbackAnalytics").then(m => ({ default: m.FeedbackAnalytics })));

// Partner Elevated Services
const PartnerGuardiansPageLazy = React.lazy(() => import("./pages/partner/GuardiansPage"));
const PartnerDigitalCustodyPageLazy = React.lazy(() => import("./pages/partner/DigitalCustodyPage"));
const PartnerHealthcarePageLazy = React.lazy(() => import("./pages/partner/HealthcarePage"));
const PartnerNotarizationPageLazy = React.lazy(() => import("./pages/partner/NotarizationPage"));
const PartnerBeneficiaryPageLazy = React.lazy(() => import("./pages/partner/BeneficiaryPage"));

// Enterprise pages
const OrganizationsPageLazy = React.lazy(() => import("@/pages/enterprise/OrganizationsPage"));
const SSOConfigurationPageLazy = React.lazy(() => import("@/pages/enterprise/SSOConfigurationPage"));
const WhiteLabelPageLazy = React.lazy(() => import("@/pages/enterprise/WhiteLabelPage"));
const EnterpriseMembersPageLazy = React.lazy(() => import("@/pages/enterprise/MembersPage"));
const EnterpriseTeamsPageLazy = React.lazy(() => import("@/pages/enterprise/TeamsPage"));
const EnterpriseAuditLogPageLazy = React.lazy(() => import("@/pages/enterprise/AuditLogPage"));
const EnterpriseSettingsPageLazy = React.lazy(() => import("@/pages/enterprise/SettingsPage"));

// Enterprise Elevated Services
const EnterpriseGuardiansPageLazy = React.lazy(() => import("./pages/enterprise/GuardiansPage"));
const EnterpriseDigitalCustodyPageLazy = React.lazy(() => import("./pages/enterprise/DigitalCustodyPage"));
const EnterpriseHealthcarePageLazy = React.lazy(() => import("./pages/enterprise/HealthcarePage"));
const EnterpriseNotarizationPageLazy = React.lazy(() => import("./pages/enterprise/NotarizationPage"));
const EnterpriseBeneficiaryPageLazy = React.lazy(() => import("./pages/enterprise/BeneficiaryPage"));

// Demo pages (public, no authentication required)
const DemoViewPageLazy = React.lazy(() => import("@/pages/public/DemoViewPage"));

// Fallback
import NotFound from "@/pages/NotFound";

/**
 * Shared Suspense fallback component for faster perceived load times
 * Shows minimal loading UI while route chunks are being fetched
 */
const SuspenseFallback = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="flex flex-col items-center gap-4">
      <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  </div>
);

/**
 * Creates a wrapped route component with Suspense boundary
 * for consistent loading states across lazy-loaded routes
 */
const LazyRoute = (
  Component: React.LazyExoticComponent<React.ComponentType<Record<string, never>>>
) => (
  <React.Suspense fallback={<SuspenseFallback />}>
    <Component />
  </React.Suspense>
);


/**
 * Root application component that configures all providers and routing
 *
 * This component wraps the entire application with necessary context providers
 * and sets up the routing structure. It uses React Router for client-side routing
 * and implements lazy loading for performance optimization.
 *
 * @returns {JSX.Element} The application root with all providers and routes
 */
const App = () => {
  React.useEffect(() => {
    console.log("App component mounted");
  }, []);

  return (
    <ErrorBoundary>
      <I18nProvider>
        <ThemeProvider>
        <BrowserRouter>
          <SessionProvider>
            <QueryProvider>
              <FeedbackWidgetProvider>
                <TooltipProvider>
                  <Toaster />
                  <Sonner />
                  <Routes>
                  {/* Public routes */}
                  <Route element={<PublicLayout />}>
                    <Route path="/" element={<React.Suspense fallback={<div className="flex items-center justify-center min-h-screen">Loading...</div>}><HomePage /></React.Suspense>} />
                    <Route path="/app/plan-demo" element={<React.Suspense fallback={<SuspenseFallback />}><PlanDemoLazy /></React.Suspense>} />
                    <Route path="/demo/:type" element={<React.Suspense fallback={<SuspenseFallback />}><DemoViewPageLazy /></React.Suspense>} />
                    <Route path="/how-it-works" element={<React.Suspense fallback={<SuspenseFallback />}><HowItWorksPageLazy /></React.Suspense>} />
                    <Route path="/trust" element={<React.Suspense fallback={<SuspenseFallback />}><TrustPageLazy /></React.Suspense>} />
                    <Route path="/about" element={<React.Suspense fallback={<SuspenseFallback />}><AboutPageLazy /></React.Suspense>} />
                    <Route path="/individuals" element={<React.Suspense fallback={<SuspenseFallback />}><IndividualsPageLazy /></React.Suspense>} />
                    <Route path="/institutions" element={<React.Suspense fallback={<SuspenseFallback />}><InstitutionsPageLazy /></React.Suspense>} />
                    <Route path="/partners" element={<React.Suspense fallback={<SuspenseFallback />}><PartnersPageLazy /></React.Suspense>} />
                    <Route path="/status" element={<React.Suspense fallback={<SuspenseFallback />}><StatusPageLazy /></React.Suspense>} />
                    <Route path="/insights" element={<React.Suspense fallback={<SuspenseFallback />}><InsightsPageLazy /></React.Suspense>} />
                    <Route path="/privacy" element={<React.Suspense fallback={<SuspenseFallback />}><PrivacyPageLazy /></React.Suspense>} />
                    <Route path="/terms" element={<React.Suspense fallback={<SuspenseFallback />}><TermsPageLazy /></React.Suspense>} />
                    <Route path="/cookies" element={<React.Suspense fallback={<SuspenseFallback />}><CookiesPageLazy /></React.Suspense>} />
                    <Route path="/ai-statement" element={<React.Suspense fallback={<SuspenseFallback />}><AIStatementPageLazy /></React.Suspense>} />
                    <Route path="/pricing" element={<React.Suspense fallback={<SuspenseFallback />}><PricingPageLazy /></React.Suspense>} />
                    <Route path="/help" element={<React.Suspense fallback={<SuspenseFallback />}><HelpPageLazy /></React.Suspense>} />
                    <Route path="/verify/:token" element={<React.Suspense fallback={null}><VerifyProofPackPageLazy /></React.Suspense>} />
                  </Route>

                  {/* Auth routes - only accessible to unauthenticated users */}
                  <Route element={<RequireUnauthenticated><AuthLayout /></RequireUnauthenticated>}>
                    <Route path="/login" element={<React.Suspense fallback={<SuspenseFallback />}><LoginPageLazy /></React.Suspense>} />
                    <Route path="/register" element={<React.Suspense fallback={<SuspenseFallback />}><RegisterPageLazy /></React.Suspense>} />
                    {/* Universal organization signup - new primary route */}
                    <Route path="/register/organization" element={<React.Suspense fallback={null}><UniversalOrganizationSignupPageLazy /></React.Suspense>} />
                    {/* Legacy routes - redirect to universal signup for backward compatibility */}
                    <Route path="/register/institution" element={<React.Suspense fallback={null}><InstitutionSignupRedirectLazy /></React.Suspense>} />
                    <Route path="/register/partner" element={<React.Suspense fallback={null}><PartnerSignupRedirectLazy /></React.Suspense>} />
                  </Route>

                  {/* App routes (consumer) */}
                  <Route path="/app" element={<AppLayout />}>
                    <Route index element={<React.Suspense fallback={null}><DashboardPage /></React.Suspense>} />
                    <Route path="profile" element={<RequireAuth><React.Suspense fallback={null}><ProfilePageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="assets" element={<RequireAuth><React.Suspense fallback={null}><AssetsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="roles" element={<RequireAuth><React.Suspense fallback={null}><RolesPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="triggers" element={<RequireAuth><React.Suspense fallback={null}><TriggersPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="instructions" element={<RequireAuth><React.Suspense fallback={null}><InstructionsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="activity" element={<RequireAuth><React.Suspense fallback={null}><ActivityPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="institution-case" element={<React.Suspense fallback={null}><InstitutionCasePageLazy /></React.Suspense>} />
                    {/* plan-demo is now public (see public routes) */}
                    <Route path="editor" element={<RequireAuth><React.Suspense fallback={null}><PlanEditorLazy /></React.Suspense></RequireAuth>} />
                    <Route path="dependents" element={<RequireAuth><React.Suspense fallback={null}><DependentsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="documents" element={<RequireAuth><React.Suspense fallback={null}><DocumentsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="simulate" element={<RequireAuth><React.Suspense fallback={null}><SimulationPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="settings" element={<RequireAuth><React.Suspense fallback={null}><SettingsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="executor-tasks" element={<RequireAuth><React.Suspense fallback={null}><ExecutorTasksPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="playbook-templates" element={<RequireAuth><React.Suspense fallback={null}><PlaybookTemplatesPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="proof-packs" element={<RequireAuth><React.Suspense fallback={null}><ProofPacksPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="capsules" element={<RequireAuth><React.Suspense fallback={null}><CapsulesPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="emergency-protocols" element={<RequireAuth><React.Suspense fallback={null}><EmergencyProtocolsPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="witnesses" element={<RequireAuth><React.Suspense fallback={null}><WitnessesPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="trust-network" element={<RequireAuth><React.Suspense fallback={null}><TrustNetworkPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="authority-state" element={<RequireAuth><React.Suspense fallback={null}><AuthorityStatePageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="absence-status" element={<RequireAuth><React.Suspense fallback={null}><AbsenceStatusPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="compliance" element={<RequireAuth><React.Suspense fallback={null}><CompliancePageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="invites/accept" element={<React.Suspense fallback={null}><AcceptInviteLazy /></React.Suspense>} />

                    {/* Elevated domain routes */}
                    <Route path="guardians" element={<RequireAuth><React.Suspense fallback={null}><GuardiansPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="digital-custody" element={<RequireAuth><React.Suspense fallback={null}><DigitalCustodyPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="healthcare" element={<RequireAuth><React.Suspense fallback={null}><HealthcarePageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="notarization" element={<RequireAuth><React.Suspense fallback={null}><NotarizationPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="beneficiary" element={<RequireAuth><React.Suspense fallback={null}><BeneficiaryDashboardPageLazy /></React.Suspense></RequireAuth>} />

                    {/* Institutional evaluation routes */}
                    <Route path="institutional-dashboard" element={<RequireAuth><React.Suspense fallback={null}><InstitutionalDashboardPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="institutional-intake" element={<RequireAuth><React.Suspense fallback={null}><InstitutionalIntakePageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="institutional-submission-confirmed" element={<RequireAuth><React.Suspense fallback={null}><InstitutionalSubmissionConfirmedPageLazy /></React.Suspense></RequireAuth>} />
                    <Route path="example-scenario" element={<RequireAuth><React.Suspense fallback={null}><ExampleScenarioPageLazy /></React.Suspense></RequireAuth>} />
                  </Route>

                  {/* Partner routes */}
                  <Route path="/partner" element={<RequireAuth><RequireRole allowed={[...PARTNER_ROLES, ...ADMIN_ROLES]}><PartnerLayout /></RequireRole></RequireAuth>}>
                    <Route index element={<React.Suspense fallback={null}><PartnerDashboardProductionLazy /></React.Suspense>} />
                    <Route path="system" element={<React.Suspense fallback={null}><PartnerSystemDashboardLazy /></React.Suspense>} />
                    <Route path="api-keys" element={<React.Suspense fallback={null}><IntegrationsPageLazy /></React.Suspense>} />
                    <Route path="webhooks" element={<React.Suspense fallback={null}><PartnerWebhooksPageLazy /></React.Suspense>} />
                    <Route path="docs" element={<React.Suspense fallback={null}><PartnerDocumentationPageLazy /></React.Suspense>} />
                    <Route path="users" element={<React.Suspense fallback={null}><PartnerUsersPageLazy /></React.Suspense>} />
                    <Route path="cases" element={<React.Suspense fallback={null}><CasesPageLazy /></React.Suspense>} />
                    <Route path="cases/:caseId" element={<React.Suspense fallback={null}><CaseDetailPageLazy /></React.Suspense>} />
                    <Route path="audits" element={<React.Suspense fallback={null}><AuditsPageLazy /></React.Suspense>} />
                    <Route path="jurisdictions" element={<React.Suspense fallback={null}><JurisdictionsPageLazy /></React.Suspense>} />
                    <Route path="escalations" element={<React.Suspense fallback={null}><EscalationsPageLazy /></React.Suspense>} />
                    <Route path="billing" element={<React.Suspense fallback={null}><BillingPageLazy /></React.Suspense>} />
                    <Route path="settings" element={<React.Suspense fallback={null}><PartnerSettingsPageLazy /></React.Suspense>} />
                    <Route path="incidents" element={<React.Suspense fallback={null}><PartnerIncidentsPageLazy /></React.Suspense>} />
                    <Route path="guardians" element={<React.Suspense fallback={null}><PartnerGuardiansPageLazy /></React.Suspense>} />
                    <Route path="digital-custody" element={<React.Suspense fallback={null}><PartnerDigitalCustodyPageLazy /></React.Suspense>} />
                    <Route path="healthcare" element={<React.Suspense fallback={null}><PartnerHealthcarePageLazy /></React.Suspense>} />
                    <Route path="notarization" element={<React.Suspense fallback={null}><PartnerNotarizationPageLazy /></React.Suspense>} />
                    <Route path="beneficiary" element={<React.Suspense fallback={null}><PartnerBeneficiaryPageLazy /></React.Suspense>} />
                  </Route>

                  {/* Admin routes */}
                  <Route path="/admin" element={<RequireAuth><RequireRole allowed={["platform_admin"]}><AdminLayout /></RequireRole></RequireAuth>}>
                    <Route index element={<React.Suspense fallback={<SuspenseFallback />}><AdminDashboardLazy /></React.Suspense>} />
                    <Route path="verifications" element={<React.Suspense fallback={<SuspenseFallback />}><VerificationsPageLazy /></React.Suspense>} />
                    <Route path="risk" element={<React.Suspense fallback={<SuspenseFallback />}><RiskPageLazy /></React.Suspense>} />
                    <Route path="partner-apps" element={<React.Suspense fallback={<SuspenseFallback />}><PartnerApplicationsPageLazy /></React.Suspense>} />
                    <Route path="partners" element={<React.Suspense fallback={<SuspenseFallback />}><PartnersManagementPageLazy /></React.Suspense>} />
                    <Route path="partners/:partnerId" element={<React.Suspense fallback={<SuspenseFallback />}><PartnerDetailPageLazy /></React.Suspense>} />
                    <Route path="partners/:partnerId/keys" element={<React.Suspense fallback={<SuspenseFallback />}><PartnerDetailPageLazy /></React.Suspense>} />
                    <Route path="partners/:partnerId/webhooks" element={<React.Suspense fallback={<SuspenseFallback />}><AdminPartnerWebhooksPageLazy /></React.Suspense>} />
                    <Route path="incidents" element={<React.Suspense fallback={<SuspenseFallback />}><IncidentsPageLazy /></React.Suspense>} />
                    <Route path="objections" element={<React.Suspense fallback={<SuspenseFallback />}><ObjectionsManagementPageLazy /></React.Suspense>} />
                    <Route path="public-status" element={<React.Suspense fallback={<SuspenseFallback />}><PublicStatusPublisherLazy /></React.Suspense>} />
                    <Route path="audits" element={<React.Suspense fallback={<SuspenseFallback />}><AuditLogsLazy /></React.Suspense>} />
                    <Route path="exports" element={<React.Suspense fallback={<SuspenseFallback />}><ExportsPageLazy /></React.Suspense>} />
                    <Route path="promotions" element={<React.Suspense fallback={<SuspenseFallback />}><PromotionsPageLazy /></React.Suspense>} />
                    <Route path="policies" element={<React.Suspense fallback={<SuspenseFallback />}><DataGovernancePageLazy /></React.Suspense>} />
                    <Route path="deletion-jobs" element={<React.Suspense fallback={null}><DeletionJobsPageLazy /></React.Suspense>} />
                    <Route path="integrity" element={<React.Suspense fallback={null}><IntegrityDashboardLazy /></React.Suspense>} />
                    <Route path="security" element={<React.Suspense fallback={null}><SecurityDashboardLazy /></React.Suspense>} />
                    <Route path="api" element={<React.Suspense fallback={null}><ApiManagementPageLazy /></React.Suspense>} />
                    <Route path="analytics" element={<React.Suspense fallback={null}><AnalyticsPageLazy /></React.Suspense>} />
                    <Route path="workers" element={<React.Suspense fallback={null}><WorkerHealthPageLazy /></React.Suspense>} />
                    <Route path="geoip" element={<React.Suspense fallback={null}><GeoIPMetricsDashboardLazy /></React.Suspense>} />
                    <Route path="feedback" element={<React.Suspense fallback={null}><FeedbackManagerLazy /></React.Suspense>} />
                    <Route path="feedback/analytics" element={<React.Suspense fallback={null}><FeedbackAnalyticsLazy /></React.Suspense>} />
                    <Route path="config" element={<React.Suspense fallback={null}><AdminSettingsPageLazy /></React.Suspense>} />
                    <Route path="settings" element={<React.Suspense fallback={null}><AdminSettingsPageLazy /></React.Suspense>} />
                  </Route>

                  {/* Enterprise routes */}
                  <Route path="/enterprise" element={<RequireAuth><RequireRole allowed={["org_owner", "org_admin", "platform_admin"]}><EnterpriseLayout /></RequireRole></RequireAuth>}>
                    <Route index element={<React.Suspense fallback={null}><EnterpriseSystemDashboardLazy /></React.Suspense>} />
                    <Route path="system" element={<React.Suspense fallback={null}><EnterpriseSystemDashboardLazy /></React.Suspense>} />
                    <Route path="organizations" element={<React.Suspense fallback={null}><OrganizationsPageLazy /></React.Suspense>} />
                    <Route path="members" element={<React.Suspense fallback={null}><EnterpriseMembersPageLazy /></React.Suspense>} />
                    <Route path="teams" element={<React.Suspense fallback={null}><EnterpriseTeamsPageLazy /></React.Suspense>} />
                    <Route path="sso" element={<React.Suspense fallback={null}><SSOConfigurationPageLazy /></React.Suspense>} />
                    <Route path="branding" element={<React.Suspense fallback={null}><WhiteLabelPageLazy /></React.Suspense>} />
                    <Route path="audits" element={<React.Suspense fallback={null}><EnterpriseAuditLogPageLazy /></React.Suspense>} />
                    <Route path="settings" element={<React.Suspense fallback={null}><EnterpriseSettingsPageLazy /></React.Suspense>} />
                    <Route path=":orgSlug/sso" element={<React.Suspense fallback={null}><SSOConfigurationPageLazy /></React.Suspense>} />
                    <Route path=":orgSlug/branding" element={<React.Suspense fallback={null}><WhiteLabelPageLazy /></React.Suspense>} />
                    <Route path="guardians" element={<React.Suspense fallback={null}><EnterpriseGuardiansPageLazy /></React.Suspense>} />
                    <Route path="digital-custody" element={<React.Suspense fallback={null}><EnterpriseDigitalCustodyPageLazy /></React.Suspense>} />
                    <Route path="healthcare" element={<React.Suspense fallback={null}><EnterpriseHealthcarePageLazy /></React.Suspense>} />
                    <Route path="notarization" element={<React.Suspense fallback={null}><EnterpriseNotarizationPageLazy /></React.Suspense>} />
                    <Route path="beneficiary" element={<React.Suspense fallback={null}><EnterpriseBeneficiaryPageLazy /></React.Suspense>} />
                  </Route>

                  {/* Catch-all */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </TooltipProvider>
              </FeedbackWidgetProvider>
            </QueryProvider>
          </SessionProvider>
        </BrowserRouter>
      </ThemeProvider>
    </I18nProvider>
  </ErrorBoundary>
  );
};

export default App;
